import { STORY_VERSIONS } from 'common/constants';
import {
	capturePropertyChanges,
	StoryEditCaptureError,
	type PropertyChangeDocument,
} from 'common/story-edit-operations/property-patch';
import type { StoryEditChange, StoryListChange } from 'common/story-edit-operations/types';
import { collectionChanges, pairedItemDocuments, StorySide } from './collection-capture';

// Edit capture: compares the story autosave persists before and after one local write.
// Ordered collections change by ID-anchored list operations; steps, cards and building blocks
// present on both sides change by property patches addressed by ID.

/** Outcome of capturing one save-bound write. */
export type EditCaptureResult =
	| { status: 'captured'; changes: StoryEditChange[] }
	| { status: 'noop' } // nothing persisted changed
	| { status: 'unsupported'; reason: string };

/** Marks a save-bound write that must not be captured (local AI, voluntary updates). */
export type EditCaptureMark = 'excluded';

/** Story as autosave persists it; only its latest version is edited. */
export type StorySnapshot = {
	storyVersions?: { [STORY_VERSIONS.latest]?: { settings?: unknown; data?: unknown } };
} & Record<string, unknown>;

type Model = Record<string, unknown>;

const isRecord = (value: unknown): value is Model =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const idOf = (value: unknown) =>
	isRecord(value) && typeof value._id === 'string' && value._id ? value._id : undefined;

const memberOf = (value: unknown, key: string) => (isRecord(value) ? value[key] : undefined);

/**
 * Changes in application order: story, settings and data properties; list operations (structure
 * first, so later patches find every item where the write left it); then properties of the
 * steps, cards and blocks present on both sides.
 * Card editor writes are compared on the story merged with the edited card, as their autosave persists it.
 */
export function captureStorySave(
	before: StorySnapshot | null | undefined,
	after: StorySnapshot | null | undefined
): EditCaptureResult {
	const { storyVersions: beforeVersions, ...beforeStory } = before ?? {};
	const { storyVersions: afterVersions, ...afterStory } = after ?? {};
	const beforeLatest = beforeVersions?.[STORY_VERSIONS.latest];
	const afterLatest = afterVersions?.[STORY_VERSIONS.latest];
	try {
		const data = dataChanges(beforeLatest?.data, afterLatest?.data);
		const changes = [
			...propertyChanges([
				{ target: { kind: 'story' }, before: beforeStory, after: afterStory },
				{ target: { kind: 'settings' }, before: beforeLatest?.settings, after: afterLatest?.settings },
				...data.properties,
			]),
			...data.lists,
			...propertyChanges(data.items),
		];
		return changes.length ? { status: 'captured', changes } : { status: 'noop' };
	} catch (error) {
		if (!(error instanceof StoryEditCaptureError)) throw error;
		return { status: 'unsupported', reason: error.message };
	}
}

/** A document absent on both sides (e.g. no settings loaded yet) has nothing to compare. */
const propertyChanges = (documents: PropertyChangeDocument[]) =>
	capturePropertyChanges(documents.filter(document => document.before !== undefined || document.after !== undefined));

type DataChanges = {
	properties: PropertyChangeDocument[]; // `data` members outside the compared collections
	lists: StoryListChange[];
	items: PropertyChangeDocument[]; // steps, cards, blocks and instance overrides on both sides
};

/**
 * Only collections whose container changed are compared: immer keeps everything else by reference.
 * When an item cannot be addressed (a repeated or missing key), the data is compared as properties.
 */
function dataChanges(before: unknown, after: unknown): DataChanges {
	if (before === after || !isRecord(before) || !isRecord(after)) {
		return { properties: [{ target: { kind: 'data' }, before, after }], lists: [], items: [] };
	}
	const scope = changedScope(before, after);
	const beforeSide = indexSide(before, scope);
	const afterSide = indexSide(after, scope, beforeSide);
	if (beforeSide.isAmbiguous || afterSide.isAmbiguous) {
		return { properties: [{ target: { kind: 'data' }, before, after }], lists: [], items: [] };
	}

	const symbols = symbolDocuments(before.symbols, after.symbols, scope.masters);
	const rest = (data: Model, symbolsRest: unknown) => ({
		...data,
		steps: undefined,
		elements: undefined,
		symbols: symbolsRest,
	});
	return {
		properties: [
			{ target: { kind: 'data' }, before: rest(before, symbols.before), after: rest(after, symbols.after) },
		],
		lists: collectionChanges(beforeSide, afterSide),
		items: [...pairedItemDocuments(beforeSide, afterSide), ...symbols.documents],
	};
}

type Scope = {
	steps: boolean;
	stepIds: Set<string>;
	cardIds: Set<string>;
	elements: boolean;
	masters: Set<string>; // symbols whose master changed but kept its root block
};

const stepsOf = (data: Model): unknown[] => (Array.isArray(data.steps) ? data.steps : []);

const cardsOf = (data: Model): unknown[] =>
	stepsOf(data).flatMap(step => {
		const cards = memberOf(step, 'cards');
		return Array.isArray(cards) ? cards : [];
	});

/** IDs of the items that differ by reference, including items present on one side only. */
function changedIds(before: unknown[], after: unknown[]): Set<string> {
	const previous = new Map(before.map(item => [idOf(item), item]));
	const changed = new Set<string>();
	for (const item of after) {
		const id = idOf(item);
		if (id && previous.get(id) !== item) changed.add(id);
		previous.delete(id);
	}
	previous.forEach((_, id) => id && changed.add(id));
	return changed;
}

function changedScope(before: Model, after: Model): Scope {
	const masters = new Set<string>();
	if (isRecord(before.symbols) && isRecord(after.symbols) && before.symbols !== after.symbols) {
		for (const [symbolId, symbol] of Object.entries(after.symbols)) {
			const previousMaster = memberOf(before.symbols[symbolId], 'master');
			const master = memberOf(symbol, 'master');
			if (previousMaster !== master && idOf(previousMaster) && idOf(previousMaster) === idOf(master)) {
				masters.add(symbolId);
			}
		}
	}
	return {
		steps: before.steps !== after.steps,
		stepIds: changedIds(stepsOf(before), stepsOf(after)),
		cardIds: changedIds(cardsOf(before), cardsOf(after)),
		elements: before.elements !== after.elements,
		masters,
	};
}

/** The compared collections of one side, parents before children. */
function indexSide(data: Model, scope: Scope, previous?: StorySide): StorySide {
	const side = new StorySide(previous);
	if (scope.steps) side.addCollection({ kind: 'data', list: 'steps' }, data.steps);
	for (const step of stepsOf(data)) {
		const stepId = idOf(step);
		if (stepId && scope.stepIds.has(stepId)) side.addCollection({ kind: 'step', stepId }, memberOf(step, 'cards'));
	}
	for (const card of cardsOf(data)) {
		const cardId = idOf(card);
		if (cardId && scope.cardIds.has(cardId))
			side.addCollection({ kind: 'card', cardId }, memberOf(card, 'elements'));
	}
	if (scope.elements) side.addCollection({ kind: 'data', list: 'elements' }, data.elements);
	const symbols = isRecord(data.symbols) ? data.symbols : {};
	scope.masters.forEach(symbolId => side.addMasterRoot(symbolId, memberOf(symbols[symbolId], 'master')));
	return side;
}

/** Precise documents plus the members they do not cover, which stay in the `data` comparison. */
type PartialDocuments = { documents: PropertyChangeDocument[]; before: unknown; after: unknown };

/**
 * Symbols present on both sides: compared masters are left to the collections, instance
 * overrides are compared by position. Added or removed symbols and instances stay in `data`.
 */
function symbolDocuments(before: unknown, after: unknown, masters: Set<string>): PartialDocuments {
	if (before === after || !isRecord(before) || !isRecord(after)) return { documents: [], before, after };
	const documents: PropertyChangeDocument[] = [];
	const restBefore: Model = { ...before };
	const restAfter: Model = { ...after };
	for (const [symbolId, symbol] of Object.entries(after)) {
		const previous = before[symbolId];
		if (previous === symbol || !isRecord(previous) || !isRecord(symbol)) continue;

		const instances = instanceDocuments(symbolId, previous.instance, symbol.instance, symbol.master);
		documents.push(...instances.documents);
		const masterRest = masters.has(symbolId) && { master: undefined };
		restBefore[symbolId] = withMembers(previous, { ...masterRest, instance: instances.before });
		restAfter[symbolId] = withMembers(symbol, { ...masterRest, instance: instances.after });
	}
	return { documents, before: restBefore, after: restAfter };
}

/** Instances present on both sides; an override that cannot be compared by block stays whole. */
function instanceDocuments(symbolId: string, before: unknown, after: unknown, master: unknown): PartialDocuments {
	if (before === after || !isRecord(before) || !isRecord(after)) return { documents: [], before, after };
	const documents: PropertyChangeDocument[] = [];
	const restBefore: Model = { ...before };
	const restAfter: Model = { ...after };
	for (const [instanceId, override] of Object.entries(after)) {
		const previous = before[instanceId];
		if (previous === override || !isRecord(previous) || !isRecord(override)) continue;
		const owner = { kind: 'symbol-instance', symbolId, instanceId } as const;
		const overrides = overrideDocuments(owner, previous, override, master);
		if (!overrides) continue;
		documents.push(...overrides);
		delete restBefore[instanceId];
		delete restAfter[instanceId];
	}
	return { documents, before: restBefore, after: restAfter };
}

/** Shallow copy with some members replaced; `undefined` members are left out of the comparison. */
function withMembers(value: unknown, members: Model): unknown {
	return isRecord(value) ? { ...value, ...members } : value;
}

/** Block properties; children are compared as a list or as text, never with the properties. */
const propertiesOf = (block: Model) => ({ ...block, children: undefined });

/**
 * Instance overrides are sparse copies of the master tree. A block is addressed by its own `_id`,
 * otherwise by the master block at the same position. When an override block was removed or now
 * overrides another block, `undefined`: the whole override is compared instead.
 */
function overrideDocuments(
	owner: { kind: 'symbol-instance'; symbolId: string; instanceId: string },
	before: Model,
	after: Model,
	master: unknown
): PropertyChangeDocument[] | undefined {
	const beforeBlocks = overrideBlocks(before, master);
	const afterBlocks = overrideBlocks(after, master);
	if (!beforeBlocks || !afterBlocks) return undefined;
	for (const [position, { elementId }] of beforeBlocks) {
		if (afterBlocks.get(position)?.elementId !== elementId) return undefined;
	}

	const documents: PropertyChangeDocument[] = [];
	for (const [position, { elementId, block }] of afterBlocks) {
		const previous = beforeBlocks.get(position)?.block;
		if (previous === block) continue;
		if (previous && Array.isArray(previous.children) !== Array.isArray(block.children)) return undefined;
		const target = { kind: 'element', owner, elementId } as const;
		documents.push({ target, before: propertiesOf(previous ?? {}), after: propertiesOf(block) });
		// Text content override; override block lists are walked by position instead.
		if (!Array.isArray(block.children) && !Array.isArray(previous?.children)) {
			documents.push({ target, before: { children: previous?.children }, after: { children: block.children } });
		}
	}
	return documents;
}

/** Override blocks by position (`.0.2`) with the ID of the block each one overrides. */
function overrideBlocks(root: Model, master: unknown): Map<string, { elementId: string; block: Model }> | undefined {
	const blocks = new Map<string, { elementId: string; block: Model }>();
	const visit = (block: unknown, masterBlock: unknown, position: string): boolean => {
		if (!isRecord(block)) return true; // no override at this position
		const elementId = idOf(block) ?? idOf(masterBlock);
		if (!elementId) return false;
		blocks.set(position, { elementId, block });
		if (!Array.isArray(block.children)) return true;
		const masterChildren = memberOf(masterBlock, 'children');
		// `every` skips the holes of sparse override lists.
		return block.children.every((child, index) =>
			visit(child, Array.isArray(masterChildren) ? masterChildren[index] : undefined, `${position}.${index}`)
		);
	};
	return visit(root, master, '') ? blocks : undefined;
}
